import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchServiceProviderForUser, isUuidLike } from "@/services/providers/providerCatalogService";
import { getPlatformProvider } from "@/services/platformApi/publicCatalogApi";
import { Provider } from "@/types/supabase";
import { useAuth } from "@/contexts/auth";
import { debugLogger } from '@/utils/debug';

export function useServiceProvider(serviceId: string | undefined) {
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const viewerKey = user?.id ?? 'anonymous';
  const isResolvableUuid = serviceId ? isUuidLike(serviceId) : false;

  const query = useQuery({
    queryKey: ['serviceProvider', serviceId, viewerKey, isAdmin],
    queryFn: async () => {
      if (!serviceId) {
        debugLogger.log("No serviceId provided to useServiceProvider");
        return null;
      }
      
      debugLogger.log("Fetching provider with route identifier:", serviceId);
      
      try {
        const provider = isAdmin
          ? await fetchServiceProviderForUser(serviceId, isAdmin)
          : await getPlatformProvider(serviceId);
        debugLogger.log("Provider data received:", provider);
        return provider as Provider;
      } catch (error) {
        console.error("Error fetching provider:", error);
        throw error;
      }
    },
    enabled: !!serviceId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  queryClient.setQueryDefaults(['serviceProvider', serviceId, viewerKey, isAdmin], {
    staleTime: 5 * 60 * 1000,
  });

  return query;
}
